/**
 * RESPONSIBILITY: the ACFI palette and type.
 *
 * The seven colours are the brand deck's own values, not an interpretation of them.
 * Everything else in the stylesheet is mixed from these with color-mix, so changing one
 * here moves every tint, border and hover state that was derived from it.
 *
 * These become CSS custom properties (see app/web/theme-vars.ts), which is why editing a
 * colour recolours the page without re-rendering a single component.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only. Listing admin would be wrong:
 * it bypasses every check by definition.
 */
import { Section, Color, Choice, Num } from '@framework/core/decorators';
import { SectionName } from '../section-names';

@Section({ name: SectionName.Theme, label: 'Site Theme' })
export class ThemeSection {
  @Color({ label: 'Primary', default: '#2F5972', group: 'Brand colours' })
  primary!: string;

  @Color({ label: 'Secondary', default: '#F4AA0A', group: 'Brand colours' })
  secondary!: string;

  @Color({ label: 'Cream (page)', default: '#FFFFFB', group: 'Neutrals' })
  cream!: string;

  @Color({ label: 'Mist', default: '#EFF2F0', group: 'Neutrals' })
  mist!: string;

  @Color({ label: 'Sand', default: '#E8E1CC', group: 'Neutrals' })
  sand!: string;

  @Color({ label: 'Coral', default: '#FB7581', group: 'Accents' })
  coral!: string;

  @Color({ label: 'Olive', default: '#BEB950', group: 'Accents' })
  olive!: string;

  /**
   * Hagrid and Anaktoria are the deck's faces, and both are Canva-licensed with no
   * webfont release we can self-host. These are the stand-ins until that is settled —
   * which is why they are a Choice and not baked into the stylesheet.
   *
   * Manrope and Inter are the two that are actually SHIPPED with the site (app/web/fonts,
   * self-hosted), so they look the same on every visitor's machine. The others are named
   * and used only where the visitor happens to have them installed.
   */
  @Choice({
    label: 'Display font',
    options: ['Manrope', 'Poppins', 'Segoe UI', 'Optima', 'Avenir', 'system-ui'],
    default: 'Poppins',
    group: 'Type',
  })
  fontDisplay!: string;

  @Choice({
    label: 'Body font',
    options: ['Inter', 'Cormorant Garamond', 'Georgia', 'Iowan Old Style', 'Palatino'],
    default: 'Cormorant Garamond',
    group: 'Type',
  })
  fontBody!: string;

  /** The blueprint uses square corners on cards and pills on buttons. This is cards. */
  @Num({ label: 'Corner radius (px)', min: 0, max: 24, integer: true, default: 0, group: 'Type' })
  radius!: number;
}
